// src/modules/panelGlobal/components/ClientePlataformasResumen.jsx
// Vista "Plataformas" (botón de la tabla de clientes en Requerimiento): las 3
// plataformas de rastreo de un cliente en un menú lateral — TRS Rastreo,
// Wialon y Leasecontrol, cada una desplegable en Usuarios / Unidades / Grupos —
// y un panel a la derecha que muestra solo la sección elegida.
//
// A diferencia de ClientePlataformaPanel.jsx (que arma un árbol por usuario:
// Usuario → Grupos → Unidades y es lo que sigue usando Historial de Cliente con
// sus 3 botones separados), acá se agrupa por categoría. Se reusan las mismas
// tablas (UnidadesPlataformaTable / TrsVehiculosTable), los mismos diálogos de
// alta/edición/asignación y los mismos services — solo cambia cómo se organiza.
//
// El detalle de cada plataforma se pide recién la primera vez que el usuario
// entra a alguna de sus 3 secciones (no las 3 plataformas de entrada), y una
// vez pedido queda montado (oculto con `hidden`, no desmontado) para no perder
// lo cargado ni volver a pegarle al backend al ir y volver.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, InputAdornment, List,
  ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import ComputerIcon from "@mui/icons-material/Computer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useAuth } from "../../../context/AuthContext";
import { getPanelGlobalDetalle, getPanelGlobalVehiculos } from "../services/panelGlobalService";
import {
  getWialonUserDetalle,
  listWialonUsers,
  listWialonUnits,
  listWialonHwTypes,
  listWialonIconLibrary,
  listWialonUnitGroups,
  updateWialonUnitGroup,
  updateWialonUnit,
  createWialonUser,
  createWialonUnitGroup,
  deleteWialonUser,
} from "../../wialon/services/wialonUsers";
import {
  lcGetUsuarioDetalle,
  lcListUsuarios,
  lcListUnidades,
  lcListHwTypes,
  lcListIconLibrary,
  lcListUnitGroups,
  lcCreateUsuario,
  lcListCuentas,
  lcDeleteUsuario,
  lcUpdateUnitGroup,
  lcUpdateUnidad,
  lcCreateUnitGroup,
  lcCopiarUsuarioDesdeWialon,
} from "../../leasecontrol/services/leasecontrolUsuarios.service";
import WialonUserEditDialog from "../../wialon/components/WialonUserEditDialog";
import LeasecontrolUsuarioDialog from "../../leasecontrol/components/LeasecontrolUsuarioDialog";
import GrupoFormDialog from "../../leasecontrol/components/GrupoFormDialog";
import UnidadFormDialog from "../../leasecontrol/components/UnidadFormDialog";
import AsignarGrupoDialog from "../../leasecontrol/components/AsignarGrupoDialog";
import ConfirmDeleteDialog from "../../leasecontrol/components/ConfirmDeleteDialog";
import TrsUsuarioFormDialog from "../../trsrastreo/components/TrsUsuarioFormDialog";
import TrsVehiculoFormDialog from "../../trsrastreo/components/TrsVehiculoFormDialog";
import TrsAsignarVehiculoDialog from "../../trsrastreo/components/TrsAsignarVehiculoDialog";
import TrsAsignarGrupoDialog from "../../trsrastreo/components/TrsAsignarGrupoDialog";
import {
  listarGrupos,
  listarVehiculos as listarVehiculosTrs,
  desvincularUsuarioVehiculo,
  editarGrupo as editarGrupoTrs,
} from "../../trsrastreo/services/trsRastreoService";
import {
  normalizeTrsGrupoParaDialog,
  trsListUsuariosParaGrupo,
  trsListVehiculosParaGrupo,
  trsCrearGrupoParaDialog,
  trsEditarGrupoParaDialog,
} from "../../trsrastreo/utils/trsGrupoAdapters";
import {
  EnlazarUsuarioForm,
  UnidadesPlataformaTable,
  TrsVehiculosTable,
  TrsPasswordInline,
  tableHeadCellSx,
  compactCellSx,
  GRUPO_HEADER_COLOR,
  GRUPO_HEADER_COLOR_SORT,
  GRUPO_HEADER_COLOR_SIN_GRUPO,
  cuentasDeUsuario,
} from "./ClientePlataformaPanel";

// Orden fijo a pedido: TRS Rastreo primero (y abierto por defecto), después
// Wialon y Leasecontrol.
const PLATAFORMAS = [
  { key: "trsrastreo", label: "TRS Rastreo", color: "success" },
  { key: "wialon", label: "Wialon", color: "info" },
  { key: "leasecontrol", label: "Leasecontrol", color: "secondary" },
];

const SUBSECCIONES = [
  { key: "usuarios", label: "Usuarios" },
  { key: "unidades", label: "Unidades" },
  { key: "grupos", label: "Grupos" },
];

const botonRegistrarSx = {
  whiteSpace: "nowrap",
  flexShrink: 0,
  bgcolor: GRUPO_HEADER_COLOR,
  color: "#fff",
  borderRadius: 0,
  textTransform: "none",
  fontWeight: 800,
  "&:hover": { bgcolor: GRUPO_HEADER_COLOR },
};

const contenidoHeaderSx = {
  mb: 1.5,
  pb: 1,
  borderBottom: "1px solid",
  borderColor: "divider",
};

const buscadorSx = {
  mb: 1.25,
  "& .MuiInputBase-input": { py: 0.9, fontSize: "0.85rem" },
};

// Caja de grupo (cabecera de color + tabla colapsable), misma convención de
// colores que el árbol: gris = "sin grupo", verde = tiene alguna placa SORT,
// índigo = grupo normal.
function GrupoCaja({ titulo, sinGrupo = false, tieneSort = false, open, onToggle, onMenu, extra, children }) {
  const color = sinGrupo ? GRUPO_HEADER_COLOR_SIN_GRUPO : (tieneSort ? GRUPO_HEADER_COLOR_SORT : GRUPO_HEADER_COLOR);
  return (
    <Paper variant="outlined" sx={{ borderRadius: 0, overflow: "hidden" }}>
      <Box sx={{ bgcolor: color, px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#fff", flex: 1 }}>
          {titulo}
        </Typography>
        {extra}
        {onMenu && (
          <IconButton size="small" onClick={onMenu} sx={{ p: 0.25, color: "#fff" }}>
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <IconButton size="small" onClick={onToggle} sx={{ p: 0.25, color: "#fff" }}>
          {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ p: 0.5 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

function Buscador({ value, onChange, placeholder }) {
  return (
    <TextField
      size="small"
      fullWidth
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          </InputAdornment>
        ),
      }}
      sx={buscadorSx}
    />
  );
}

const incluye = (valores, texto) =>
  valores.some((s) => String(s || "").toLowerCase().includes(texto));

// ─────────────────────────────────────────────────────────────────────────────
// TRS Rastreo
// ─────────────────────────────────────────────────────────────────────────────
function TrsSeccion({ tab, codServicioCliente, detalle, sortPlacas, recargarDetalle }) {
  const usuarios = useMemo(() => detalle?.usuarios || [], [detalle]);

  const [grupos, setGrupos] = useState([]);
  const [vehiculoInfoPorId, setVehiculoInfoPorId] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openGrupos, setOpenGrupos] = useState({});
  const [busqueda, setBusqueda] = useState("");

  const [menuAnchor, setMenuAnchor] = useState(null); // { el, usuario }
  const [grupoMenuAnchor, setGrupoMenuAnchor] = useState(null); // { el, grupo }
  const [editUsuario, setEditUsuario] = useState(null);
  const [editGrupo, setEditGrupo] = useState(null);
  const [editVehiculo, setEditVehiculo] = useState(null);
  const [asignarVehiculoUsuario, setAsignarVehiculoUsuario] = useState(null);
  const [asignarGrupoUsuario, setAsignarGrupoUsuario] = useState(null);
  const [createGrupoOpen, setCreateGrupoOpen] = useState(false);
  const [createUsuarioOpen, setCreateUsuarioOpen] = useState(false);
  const [createVehiculoOpen, setCreateVehiculoOpen] = useState(false);

  const grupoNormalizadoParaEditar = useMemo(() => normalizeTrsGrupoParaDialog(editGrupo), [editGrupo]);

  // Misma carga que TrsRastreoDetalle: cuadrillas de todo rastreo_bo (se filtran
  // por los usuarios del cliente) + listado de vehiculos para completar icono/
  // device de los que vienen por cuadrilla (cuadrilla_relacion.info_relacionado
  // es un resumen sin telemetria).
  const fetchGrupos = useCallback(() => {
    setLoading(true);
    setError("");
    return Promise.all([listarGrupos(), listarVehiculosTrs({ pageSize: 500 }).catch(() => null)])
      .then(([respGrupos, respVehiculos]) => {
        setGrupos(respGrupos?.data?.data || []);
        const completos = respVehiculos?.data?.data || [];
        setVehiculoInfoPorId(new Map(
          completos.map((v) => [String(v.id), { veh_icono: v.veh_icono, device: v.device, fecha_ultimo_registro: v.fecha_ultimo_registro }])
        ));
      })
      .catch(() => setError("No se pudieron cargar los grupos."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchGrupos(); }, [fetchGrupos]);

  const recargarTodo = () => Promise.all([fetchGrupos(), recargarDetalle?.() || Promise.resolve()]);

  const usuariosIds = useMemo(() => new Set(usuarios.map((u) => String(u.id))), [usuarios]);

  // Grupos del cliente: cuadrillas donde esta vinculado al menos uno de sus usuarios.
  const gruposCliente = useMemo(() => {
    // Los vehiculos vinculados directo a un usuario ya traen device/fecha desde
    // el backend — sirven para completar los mismos vehiculos cuando aparecen
    // por cuadrilla (mejor fuente que el listado paginado, que rastreo_bo tope a 100).
    const directoPorId = new Map();
    usuarios.forEach((u) => (u.vehiculos_relacionados || []).forEach((v) => directoPorId.set(String(v.id), v)));
    const enriquecer = (v) => {
      const directo = directoPorId.get(String(v.id));
      const extra = vehiculoInfoPorId.get(String(v.id));
      return {
        ...v,
        veh_icono: v.veh_icono || directo?.veh_icono || extra?.veh_icono || "",
        device: v.device || directo?.device || extra?.device || null,
        fecha_ultimo_registro: v.fecha_ultimo_registro || directo?.fecha_ultimo_registro || extra?.fecha_ultimo_registro || null,
      };
    };

    return grupos
      .filter((g) => (g.usuarios_vinculados || []).some((uv) => usuariosIds.has(String(uv.usuario_id))))
      .map((g) => {
        const vehiculos = (g.cuadrilla_relacion || [])
          .filter((r) => r.cuarel_activo)
          .map((r) => enriquecer({ ...r.info_relacionado, id: r.for_id }));
        const usuariosDelGrupo = usuarios.filter((u) =>
          (g.usuarios_vinculados || []).some((uv) => String(uv.usuario_id) === String(u.id))
        );
        return {
          grupo: g,
          vehiculos,
          usuariosDelGrupo,
          tieneSort: vehiculos.some((v) => sortPlacas?.has(String(v.veh_placa || "").trim().toUpperCase())),
        };
      });
  }, [grupos, usuarios, usuariosIds, vehiculoInfoPorId, sortPlacas]);

  // Unidades planas del cliente: vinculo directo usuario-vehiculo + las de sus
  // cuadrillas, sin repetir. Las directas conservan _relacionId (para poder
  // quitar el vinculo); la etiqueta de grupo sale de la primera cuadrilla que
  // lo contenga.
  const unidades = useMemo(() => {
    const grupoNombrePorVehiculo = new Map();
    gruposCliente.forEach(({ grupo, vehiculos }) =>
      vehiculos.forEach((v) => {
        if (!grupoNombrePorVehiculo.has(String(v.id))) grupoNombrePorVehiculo.set(String(v.id), grupo.cua_descripcion);
      })
    );
    const porId = new Map();
    usuarios.forEach((u) =>
      (u.vehiculos_relacionados || []).forEach((v) => {
        const id = String(v.id);
        if (!porId.has(id)) porId.set(id, { ...v, _grupoNombre: grupoNombrePorVehiculo.get(id) || "Sin grupo" });
      })
    );
    gruposCliente.forEach(({ grupo, vehiculos }) =>
      vehiculos.forEach((v) => {
        const id = String(v.id);
        if (!porId.has(id)) porId.set(id, { ...v, _grupoNombre: grupo.cua_descripcion });
      })
    );
    return Array.from(porId.values());
  }, [usuarios, gruposCliente]);

  const texto = busqueda.trim().toLowerCase();
  const unidadesFiltradas = texto
    ? unidades.filter((v) => incluye([v.veh_placa, v.veh_alias, v.veh_modid, v._grupoNombre], texto))
    : unidades;

  const gruposDeUsuario = (u) => gruposCliente.filter(({ grupo }) =>
    (grupo.usuarios_vinculados || []).some((uv) => String(uv.usuario_id) === String(u.id))
  );

  const handleDesvincularVehiculo = async (v) => {
    if (!v._relacionId) return;
    if (!window.confirm(`¿Quitar el vínculo directo con el vehículo ${v.veh_placa || v.veh_alias || v.id}?`)) return;
    try {
      await desvincularUsuarioVehiculo(v._relacionId);
      recargarTodo();
    } catch (e) {
      window.alert(e?.response?.data?.error || "No se pudo quitar el vínculo.");
    }
  };

  const handleDesvincularGrupo = async (grupoId, usuario, grupoNombre) => {
    if (!window.confirm(`¿Quitar a ${usuario.usu_username || usuario.usu_nombre} del grupo "${grupoNombre || grupoId}"?`)) return;
    try {
      await editarGrupoTrs(grupoId, { usuarios_remover: [Number(usuario.id)] });
      recargarTodo();
    } catch (e) {
      window.alert(e?.response?.data?.error || "No se pudo quitar del grupo.");
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {tab === "usuarios" && (
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Usuarios ({usuarios.length})</Typography>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateUsuarioOpen(true)} sx={botonRegistrarSx}>
              Registrar usuario
            </Button>
          </Stack>
          {!usuarios.length ? (
            <Alert severity="info">Sin usuarios vinculados.</Alert>
          ) : (
            <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, display: { xs: "none", md: "block" } }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Usuario</TableCell>
                    <TableCell sx={tableHeadCellSx}>Nombre</TableCell>
                    <TableCell sx={tableHeadCellSx}>Contraseña</TableCell>
                    <TableCell sx={tableHeadCellSx}>Estado</TableCell>
                    <TableCell sx={{ ...tableHeadCellSx, textAlign: "center" }}>Grupos</TableCell>
                    <TableCell sx={{ ...tableHeadCellSx, textAlign: "center" }}>Vehículos</TableCell>
                    <TableCell sx={{ ...tableHeadCellSx, width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuarios.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell sx={compactCellSx}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{u.usu_username || "-"}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={compactCellSx}>{u.usu_nombre || "-"}</TableCell>
                      <TableCell sx={compactCellSx}><TrsPasswordInline password={u.usu_password_descr} /></TableCell>
                      <TableCell sx={compactCellSx}>
                        {u.usu_activo === false
                          ? <Chip size="small" color="warning" label="Deshabilitado" sx={{ height: 18, fontSize: 10 }} />
                          : <Chip size="small" color="success" variant="outlined" label="Activo" sx={{ height: 18, fontSize: 10 }} />}
                      </TableCell>
                      <TableCell sx={{ ...compactCellSx, textAlign: "center" }}>{gruposDeUsuario(u).length}</TableCell>
                      <TableCell sx={{ ...compactCellSx, textAlign: "center" }}>{(u.vehiculos_relacionados || []).length}</TableCell>
                      <TableCell sx={{ ...compactCellSx, textAlign: "center" }}>
                        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, usuario: u })}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack spacing={1} sx={{ display: { xs: "flex", md: "none" } }}>
              {usuarios.map((u) => (
                <Paper key={u.id} variant="outlined" sx={{ p: 1.25, borderRadius: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="body2" noWrap sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>{u.usu_username || "-"}</Typography>
                    {u.usu_activo === false
                      ? <Chip size="small" color="warning" label="Deshabilitado" sx={{ height: 18, fontSize: 10 }} />
                      : <Chip size="small" color="success" variant="outlined" label="Activo" sx={{ height: 18, fontSize: 10 }} />}
                    <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, usuario: u })}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.5, mt: 1, alignItems: "center", fontSize: 12.5 }}>
                    <Typography variant="caption" color="text.secondary">Nombre</Typography>
                    <Box>{u.usu_nombre || "-"}</Box>
                    <Typography variant="caption" color="text.secondary">Contraseña</Typography>
                    <Box><TrsPasswordInline password={u.usu_password_descr} /></Box>
                    <Typography variant="caption" color="text.secondary">Grupos / Vehículos</Typography>
                    <Box>{gruposDeUsuario(u).length} / {(u.vehiculos_relacionados || []).length}</Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
            </>
          )}
        </Stack>
      )}

      {tab === "unidades" && (
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Unidades ({unidades.length})</Typography>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateVehiculoOpen(true)} sx={botonRegistrarSx}>
              Registrar vehículo
            </Button>
          </Stack>
          <Buscador value={busqueda} onChange={setBusqueda} placeholder="Buscar vehículo por placa, alias, modem o grupo..." />
          <TrsVehiculosTable
            vehiculos={unidadesFiltradas}
            sortPlacas={sortPlacas}
            onEditVehiculo={(v) => setEditVehiculo(v)}
            onDesvincular={handleDesvincularVehiculo}
            showGrupo
            maxHeight={420}
          />
        </Stack>
      )}

      {tab === "grupos" && (
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Grupos ({gruposCliente.length})</Typography>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateGrupoOpen(true)} sx={botonRegistrarSx}>
              Registrar grupo
            </Button>
          </Stack>
          {!gruposCliente.length && <Alert severity="info">Sin grupos vinculados a los usuarios de este cliente.</Alert>}
          {gruposCliente.map(({ grupo, vehiculos, usuariosDelGrupo, tieneSort }) => {
            const key = String(grupo.id);
            return (
              <GrupoCaja
                key={key}
                titulo={`${grupo.cua_descripcion} (${vehiculos.length})`}
                tieneSort={tieneSort}
                open={!!openGrupos[key]}
                onToggle={() => setOpenGrupos((p) => ({ ...p, [key]: !p[key] }))}
                onMenu={(e) => setGrupoMenuAnchor({ el: e.currentTarget, grupo })}
              >
                {/* Usuarios del cliente vinculados a este grupo — cada chip permite
                    quitar SOLO a ese usuario del grupo (el grupo no se borra). */}
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                  {usuariosDelGrupo.map((u) => (
                    <Chip
                      key={u.id}
                      size="small"
                      icon={<PersonIcon />}
                      label={u.usu_username || u.usu_nombre}
                      onDelete={() => handleDesvincularGrupo(grupo.id, u, grupo.cua_descripcion)}
                      deleteIcon={<Tooltip title="Quitar del grupo"><LinkOffIcon /></Tooltip>}
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  ))}
                </Stack>
                <TrsVehiculosTable
                  vehiculos={vehiculos}
                  sortPlacas={sortPlacas}
                  onEditVehiculo={(v) => setEditVehiculo(v)}
                />
              </GrupoCaja>
            );
          })}
        </Stack>
      )}

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { const u = menuAnchor?.usuario; setMenuAnchor(null); setEditUsuario(u || null); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar usuario
        </MenuItem>
        <MenuItem onClick={() => { const u = menuAnchor?.usuario; setMenuAnchor(null); setAsignarVehiculoUsuario(u || null); }}>
          <DirectionsCarIcon fontSize="small" sx={{ mr: 1 }} /> Asignar vehículo
        </MenuItem>
        <MenuItem onClick={() => { const u = menuAnchor?.usuario; setMenuAnchor(null); setAsignarGrupoUsuario(u || null); }}>
          <GroupIcon fontSize="small" sx={{ mr: 1 }} /> Asignar grupo
        </MenuItem>
      </Menu>

      <Menu anchorEl={grupoMenuAnchor?.el} open={!!grupoMenuAnchor} onClose={() => setGrupoMenuAnchor(null)}>
        <MenuItem onClick={() => { const g = grupoMenuAnchor?.grupo; setGrupoMenuAnchor(null); setEditGrupo(g || null); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar grupo
        </MenuItem>
      </Menu>

      {editUsuario && (
        <TrsUsuarioFormDialog
          open
          mode="edit"
          usuario={editUsuario}
          onClose={() => setEditUsuario(null)}
          onSaved={() => { setEditUsuario(null); recargarTodo(); }}
        />
      )}
      {asignarVehiculoUsuario && (
        <TrsAsignarVehiculoDialog
          open
          usuario={asignarVehiculoUsuario}
          onClose={() => setAsignarVehiculoUsuario(null)}
          onSaved={async () => { await recargarTodo(); setAsignarVehiculoUsuario(null); }}
        />
      )}
      {asignarGrupoUsuario && (
        <TrsAsignarGrupoDialog
          open
          usuario={asignarGrupoUsuario}
          grupos={grupos}
          onClose={() => setAsignarGrupoUsuario(null)}
          onSaved={async () => { await recargarTodo(); setAsignarGrupoUsuario(null); }}
        />
      )}
      {editVehiculo && (
        <TrsVehiculoFormDialog
          open
          mode="edit"
          vehiculo={editVehiculo}
          onClose={() => setEditVehiculo(null)}
          onSaved={() => { setEditVehiculo(null); recargarTodo(); }}
        />
      )}
      <GrupoFormDialog
        open={!!editGrupo}
        mode="edit"
        grupo={grupoNormalizadoParaEditar}
        onClose={() => setEditGrupo(null)}
        onSaved={() => { setEditGrupo(null); recargarTodo(); }}
        updateGroup={trsEditarGrupoParaDialog(editGrupo)}
        listUsers={trsListUsuariosParaGrupo}
        listUnits={trsListVehiculosParaGrupo}
        providerColor={GRUPO_HEADER_COLOR}
      />
      <GrupoFormDialog
        open={createGrupoOpen}
        mode="create"
        onClose={() => setCreateGrupoOpen(false)}
        onSaved={() => { setCreateGrupoOpen(false); recargarTodo(); }}
        createGroup={trsCrearGrupoParaDialog}
        listUsers={trsListUsuariosParaGrupo}
        listUnits={trsListVehiculosParaGrupo}
        providerColor={GRUPO_HEADER_COLOR}
        initialUserIds={usuarios.map((u) => u.id)}
      />
      <TrsVehiculoFormDialog
        open={createVehiculoOpen}
        mode="create"
        codServicioCliente={codServicioCliente}
        onClose={() => setCreateVehiculoOpen(false)}
        onSaved={() => { setCreateVehiculoOpen(false); recargarTodo(); }}
      />
      <TrsUsuarioFormDialog
        open={createUsuarioOpen}
        mode="create"
        codServicioCliente={codServicioCliente}
        onClose={() => setCreateUsuarioOpen(false)}
        onSaved={() => { setCreateUsuarioOpen(false); recargarTodo(); }}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wialon / Leasecontrol (misma estructura de detalle por usuario en ambas)
// ─────────────────────────────────────────────────────────────────────────────
function WlSeccion({ tab, plataforma, codServicioCliente, detalle, sortPlacaPorNumSim, recargarDetalle }) {
  const { user } = useAuth();
  // Vincular un usuario ya existente: por ahora solo lo puede hacer el admin
  // (se oculta al resto mientras se define el flujo definitivo).
  const esAdmin = user?.role === "admin";
  const esWialon = plataforma === "wialon";
  const externalUserIds = useMemo(() => detalle?.external_user_ids || [], [detalle]);

  const [detalleById, setDetalleById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openGrupos, setOpenGrupos] = useState({});
  const [busqueda, setBusqueda] = useState("");

  const [menuAnchor, setMenuAnchor] = useState(null); // { el, usuarioId }
  const [eliminarUsuario, setEliminarUsuario] = useState(null); // { id, nombre }
  const [eliminando, setEliminando] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState("");
  const [grupoMenuAnchor, setGrupoMenuAnchor] = useState(null); // { el, grupo }
  const [editUser, setEditUser] = useState(null); // { id, detalle }
  const [editGrupo, setEditGrupo] = useState(null);
  const [editUnidad, setEditUnidad] = useState(null);
  const [asignarGrupoUsuarioId, setAsignarGrupoUsuarioId] = useState(null);
  const [createGrupoOpen, setCreateGrupoOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const puedeCopiarALeasecontrol = esWialon && ["admin", "sistemas"].includes(user?.role);
  const [copiarUsuario, setCopiarUsuario] = useState(null); // { id, nombre }
  const [copiarNombre, setCopiarNombre] = useState("");
  const [copiarPassword, setCopiarPassword] = useState("");
  const [copiando, setCopiando] = useState(false);
  const [copiarError, setCopiarError] = useState("");
  const [copiarResultado, setCopiarResultado] = useState(null);

  const cerrarCopiar = () => {
    if (copiando) return;
    setCopiarUsuario(null);
    setCopiarPassword("");
    setCopiarError("");
    setCopiarResultado(null);
  };

  const confirmarCopiar = async () => {
    setCopiando(true);
    setCopiarError("");
    try {
      const { data } = await lcCopiarUsuarioDesdeWialon({
        wialonUserId: copiarUsuario.id,
        cod_servicio_cliente: codServicioCliente,
        nuevoNombre: copiarNombre.trim(),
        password: copiarPassword,
      });
      setCopiarResultado(data);
      setCopiarPassword("");
    } catch (e) {
      const d = e?.response?.data;
      const detalles = Array.isArray(d?.detalles) ? ` ${d.detalles.join(" ")}` : "";
      setCopiarError(`${typeof d?.error === "string" ? d.error : "No se pudo copiar el usuario."}${detalles}`);
    } finally {
      setCopiando(false);
    }
  };

  const fetchDetalleUsuario = useCallback((id) => {
    const fn = esWialon ? getWialonUserDetalle : lcGetUsuarioDetalle;
    return fn(id).then((r) => r.data);
  }, [esWialon]);

  // A diferencia del árbol (que pide el detalle de cada usuario recién al
  // expandirlo), acá las pestañas Unidades/Grupos necesitan TODOS los usuarios
  // del cliente a la vez — es un conjunto acotado por cliente, se piden en
  // paralelo una sola vez.
  const fetchTodos = useCallback(() => {
    if (!externalUserIds.length) { setDetalleById({}); setLoading(false); return Promise.resolve(); }
    setLoading(true);
    setError("");
    return Promise.all(
      externalUserIds.map((eu) =>
        fetchDetalleUsuario(eu.external_user_id)
          .then((data) => [eu.external_user_id, data])
          .catch(() => [eu.external_user_id, null])
      )
    )
      .then((pares) => setDetalleById(Object.fromEntries(pares)))
      .catch(() => setError("No se pudo cargar el detalle de los usuarios."))
      .finally(() => setLoading(false));
  }, [externalUserIds, fetchDetalleUsuario]);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const recargarUsuario = (id) =>
    fetchDetalleUsuario(id)
      .then((data) => setDetalleById((prev) => ({ ...prev, [id]: data })))
      .catch(() => {});

  // Grupos sin repetir (el mismo grupo puede colgar de varios usuarios del cliente).
  const gruposCliente = useMemo(() => {
    const porId = new Map();
    externalUserIds.forEach((eu) => {
      const det = detalleById[eu.external_user_id];
      (det?.grupos || []).forEach((g) => {
        const key = String(g.id);
        if (!porId.has(key)) porId.set(key, { grupo: g, usuariosIds: [] });
        porId.get(key).usuariosIds.push(eu.external_user_id);
      });
    });
    return Array.from(porId.values()).map((x) => ({
      ...x,
      unidades: x.grupo.unidades_detalle || [],
      tieneSort: (x.grupo.unidades_detalle || []).some((v) => sortPlacaPorNumSim.has(String(v.uid))),
    }));
  }, [externalUserIds, detalleById, sortPlacaPorNumSim]);

  const unidades = useMemo(() => {
    const porId = new Map();
    gruposCliente.forEach(({ grupo, unidades: us }) =>
      us.forEach((v) => {
        const key = String(v.id);
        if (!porId.has(key)) porId.set(key, { ...v, _grupoNombre: grupo.nm || grupo.name });
      })
    );
    externalUserIds.forEach((eu) => {
      (detalleById[eu.external_user_id]?.vehiculos_directos_sin_grupo || []).forEach((v) => {
        const key = String(v.id);
        if (!porId.has(key)) porId.set(key, { ...v, _grupoNombre: "Sin grupo" });
      });
    });
    return Array.from(porId.values());
  }, [gruposCliente, externalUserIds, detalleById]);

  const texto = busqueda.trim().toLowerCase();
  const unidadesFiltradas = texto
    ? unidades.filter((v) => incluye([v.nm, v.uid, sortPlacaPorNumSim.get(String(v.uid)), v._grupoNombre], texto))
    : unidades;

  const contarUnidades = (det) => {
    const ids = new Set();
    (det?.grupos || []).forEach((g) => (g.unidades_detalle || []).forEach((v) => ids.add(String(v.id))));
    (det?.vehiculos_directos_sin_grupo || []).forEach((v) => ids.add(String(v.id)));
    return ids.size;
  };

  // Eliminar un usuario puede arrastrar lo que creó: si afectaría a otros usuarios (los que creó o los de la cuenta que administra), no se ofrece.
  const bloqueaPorDependientes = (det) => !!det?.dependientes && (det.dependientes.usuarios_creados > 0 || det.dependientes.otros_usuarios_en_cuenta > 0 || det.dependientes.unidades_creadas > 0);

  const nombreUsuario = (id) =>
    detalleById[id]?.usuario?.nm
    || externalUserIds.find((eu) => eu.external_user_id === id)?.external_username
    || "Usuario";

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {tab === "usuarios" && (
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Usuarios ({externalUserIds.length})</Typography>
                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateUserOpen(true)} sx={botonRegistrarSx}>
                  Registrar usuario
                </Button>
              </Stack>
              {/* Vincular un usuario ya existente en la plataforma a este cliente —
                  igual que en Historial, sigue disponible aunque todavía no tenga ninguno.
                  Por ahora solo visible para admin. */}
              {esAdmin && (
                <EnlazarUsuarioForm plataforma={plataforma} codServicioCliente={codServicioCliente} onVinculado={() => recargarDetalle?.()} />
              )}
              {!externalUserIds.length ? (
                <Alert severity="info">Sin usuarios vinculados.</Alert>
              ) : (
                <>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, display: { xs: "none", md: "block" } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Usuario</TableCell>
                        {!esWialon && <TableCell sx={tableHeadCellSx}>Cuenta</TableCell>}
                        {!esWialon && <TableCell sx={tableHeadCellSx}>Creador</TableCell>}
                        <TableCell sx={tableHeadCellSx}>Contraseña</TableCell>
                        <TableCell sx={tableHeadCellSx}>Estado</TableCell>
                        <TableCell sx={{ ...tableHeadCellSx, textAlign: "center" }}>Grupos</TableCell>
                        <TableCell sx={{ ...tableHeadCellSx, textAlign: "center" }}>Unidades</TableCell>
                        <TableCell sx={{ ...tableHeadCellSx, width: 40 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {externalUserIds.map((eu) => {
                        const id = eu.external_user_id;
                        const det = detalleById[id];
                        return (
                          <TableRow key={id} hover>
                            <TableCell sx={compactCellSx}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{nombreUsuario(id)}</Typography>
                              </Stack>
                            </TableCell>
                            {!esWialon && (
                              <TableCell sx={compactCellSx}>
                                {det?.cuenta?.nombre ? (
                                  <Tooltip title={`ID de cuenta: ${det.cuenta.id}`} arrow disableInteractive>
                                    <Chip size="small" variant="outlined" icon={<AccountTreeOutlinedIcon sx={{ fontSize: 14 }} />} label={det.cuenta.nombre} sx={{ height: 20, fontSize: 11, maxWidth: 220 }} />
                                  </Tooltip>
                                ) : det?.cuenta?.id ? (
                                  <Typography variant="caption" color="text.secondary">ID {det.cuenta.id}</Typography>
                                ) : "-"}
                              </TableCell>
                            )}
                            {!esWialon && (
                              <TableCell sx={compactCellSx}>
                                {det?.creador?.nombre ? (
                                  <Tooltip title={`ID de usuario: ${det.creador.id}`} arrow disableInteractive>
                                    <Typography variant="body2" sx={{ fontSize: 12 }}>{det.creador.nombre}</Typography>
                                  </Tooltip>
                                ) : det?.creador?.id ? (
                                  <Typography variant="caption" color="text.secondary">ID {det.creador.id}</Typography>
                                ) : "-"}
                              </TableCell>
                            )}
                            <TableCell sx={compactCellSx}>
                              {det?.local?.external_password ? <TrsPasswordInline password={det.local.external_password} /> : "-"}
                            </TableCell>
                            <TableCell sx={compactCellSx}>
                              {det === null
                                ? <Chip size="small" color="error" variant="outlined" label="Sin detalle" sx={{ height: 18, fontSize: 10 }} />
                                : det?.usuario_enabled === false
                                  ? <Chip size="small" color="warning" label="Deshabilitado" sx={{ height: 18, fontSize: 10 }} />
                                  : <Chip size="small" color="success" variant="outlined" label="Activo" sx={{ height: 18, fontSize: 10 }} />}
                            </TableCell>
                            <TableCell sx={{ ...compactCellSx, textAlign: "center" }}>{(det?.grupos || []).length}</TableCell>
                            <TableCell sx={{ ...compactCellSx, textAlign: "center" }}>{contarUnidades(det)}</TableCell>
                            <TableCell sx={{ ...compactCellSx, textAlign: "center" }}>
                              <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, usuarioId: id })}>
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack spacing={1} sx={{ display: { xs: "flex", md: "none" } }}>
                  {externalUserIds.map((eu) => {
                    const id = eu.external_user_id;
                    const det = detalleById[id];
                    return (
                      <Paper key={id} variant="outlined" sx={{ p: 1.25, borderRadius: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                          <Typography variant="body2" noWrap sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>{nombreUsuario(id)}</Typography>
                          {det === null
                            ? <Chip size="small" color="error" variant="outlined" label="Sin detalle" sx={{ height: 18, fontSize: 10 }} />
                            : det?.usuario_enabled === false
                              ? <Chip size="small" color="warning" label="Deshabilitado" sx={{ height: 18, fontSize: 10 }} />
                              : <Chip size="small" color="success" variant="outlined" label="Activo" sx={{ height: 18, fontSize: 10 }} />}
                          <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, usuarioId: id })}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.5, mt: 1, alignItems: "center", fontSize: 12.5 }}>
                          {!esWialon && (
                            <>
                              <Typography variant="caption" color="text.secondary">Cuenta</Typography>
                              <Box sx={{ minWidth: 0 }}>
                                {det?.cuenta?.nombre
                                  ? <Chip size="small" variant="outlined" icon={<AccountTreeOutlinedIcon sx={{ fontSize: 14 }} />} label={det.cuenta.nombre} sx={{ height: 20, fontSize: 11, maxWidth: "100%" }} />
                                  : det?.cuenta?.id ? <Typography variant="caption" color="text.secondary">ID {det.cuenta.id}</Typography> : "-"}
                              </Box>
                              <Typography variant="caption" color="text.secondary">Creador</Typography>
                              <Box>{det?.creador?.nombre || (det?.creador?.id ? `ID ${det.creador.id}` : "-")}</Box>
                            </>
                          )}
                          <Typography variant="caption" color="text.secondary">Contraseña</Typography>
                          <Box>{det?.local?.external_password ? <TrsPasswordInline password={det.local.external_password} /> : "-"}</Box>
                          <Typography variant="caption" color="text.secondary">Grupos / Unidades</Typography>
                          <Box>{(det?.grupos || []).length} / {contarUnidades(det)}</Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
                </>
              )}
            </Stack>
          )}

          {tab === "unidades" && (
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Unidades ({unidades.length})</Typography>
              <Buscador value={busqueda} onChange={setBusqueda} placeholder="Buscar unidad por nombre, serie, placa o grupo..." />
              <UnidadesPlataformaTable
                unidades={unidadesFiltradas}
                sortPlacaPorNumSim={sortPlacaPorNumSim}
                onEditUnidad={(u) => setEditUnidad(u)}
                showGrupo
              />
            </Stack>
          )}

          {tab === "grupos" && (
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Grupos ({gruposCliente.length})</Typography>
                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateGrupoOpen(true)} sx={botonRegistrarSx}>
                  Registrar grupo
                </Button>
              </Stack>
              {!gruposCliente.length && <Alert severity="info">Sin grupos vinculados a los usuarios de este cliente.</Alert>}
              {gruposCliente.map(({ grupo, unidades: us, usuariosIds, tieneSort }) => {
                const key = String(grupo.id);
                return (
                  <GrupoCaja
                    key={key}
                    titulo={`${grupo.nm || grupo.name} (${us.length})`}
                    tieneSort={tieneSort}
                    open={!!openGrupos[key]}
                    onToggle={() => setOpenGrupos((p) => ({ ...p, [key]: !p[key] }))}
                    onMenu={(e) => setGrupoMenuAnchor({ el: e.currentTarget, grupo })}
                    extra={
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", mr: 0.5 }}>
                        {usuariosIds.map(nombreUsuario).join(", ")}
                      </Typography>
                    }
                  >
                    <UnidadesPlataformaTable
                      unidades={us}
                      sortPlacaPorNumSim={sortPlacaPorNumSim}
                      onEditUnidad={(u) => setEditUnidad(u)}
                    />
                  </GrupoCaja>
                );
              })}
            </Stack>
          )}
        </>
      )}

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          disabled={!detalleById[menuAnchor?.usuarioId]}
          onClick={() => { const id = menuAnchor?.usuarioId; setMenuAnchor(null); setEditUser({ id, detalle: detalleById[id] }); }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar usuario
        </MenuItem>
        <MenuItem onClick={() => { const id = menuAnchor?.usuarioId; setMenuAnchor(null); setAsignarGrupoUsuarioId(id || null); }}>
          <GroupIcon fontSize="small" sx={{ mr: 1 }} /> Asignar grupo
        </MenuItem>
        {/* Solo se ofrece eliminar cuando el usuario no tiene unidades (propias ni por sus grupos). */}
        {detalleById[menuAnchor?.usuarioId] && contarUnidades(detalleById[menuAnchor?.usuarioId]) === 0 && !bloqueaPorDependientes(detalleById[menuAnchor?.usuarioId]) && (
          <MenuItem
            sx={{ color: "error.main" }}
            onClick={() => {
              const id = menuAnchor?.usuarioId;
              setMenuAnchor(null);
              setErrorEliminar("");
              setEliminarUsuario({ id, nombre: nombreUsuario(id) });
            }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Eliminar usuario
          </MenuItem>
        )}
        {puedeCopiarALeasecontrol && (
          <MenuItem
            onClick={() => {
              const id = menuAnchor?.usuarioId;
              setMenuAnchor(null);
              const nombre = nombreUsuario(id);
              setCopiarNombre(nombre);
              setCopiarPassword("");
              setCopiarError("");
              setCopiarResultado(null);
              setCopiarUsuario({ id, nombre });
            }}
          >
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Copiar a Leasecontrol
          </MenuItem>
        )}
      </Menu>

      <ConfirmDeleteDialog
        open={!!eliminarUsuario}
        loading={eliminando}
        onClose={() => setEliminarUsuario(null)}
        description={
          errorEliminar
            || `¿Eliminar el usuario "${eliminarUsuario?.nombre || ""}" de ${esWialon ? "Wialon" : "Leasecontrol"}? No tiene unidades. Esta acción no se puede deshacer.${(() => {
              const dep = detalleById[eliminarUsuario?.id]?.dependientes;
              const extra = [dep?.es_admin_cuenta ? `su cuenta "${detalleById[eliminarUsuario?.id]?.cuenta?.nombre || ""}"` : "", dep?.grupos_creados ? `${dep.grupos_creados} grupo(s) que creó` : ""].filter(Boolean);
              const compartido = (detalleById[eliminarUsuario?.id]?.vinculaciones || []).length;
              const aviso = compartido > 1 ? ` Está vinculado a ${compartido} servicios de clientes: se quitará de todos.` : "";
              return aviso + (extra.length ? ` Leasecontrol puede eliminar también lo que este usuario creó (${extra.join(" y ")}).` : "");
            })()}`
        }
        onConfirm={async () => {
          setEliminando(true);
          setErrorEliminar("");
          try {
            await (esWialon ? deleteWialonUser : lcDeleteUsuario)(Number(eliminarUsuario.id));
            setEliminarUsuario(null);
            recargarDetalle?.();
          } catch (e) {
            setErrorEliminar(e?.response?.data?.error || e?.response?.data?.message || e?.message || "No se pudo eliminar el usuario.");
          } finally {
            setEliminando(false);
          }
        }}
      />

      <Dialog open={!!copiarUsuario} onClose={cerrarCopiar} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Copiar usuario a Leasecontrol</DialogTitle>
        <DialogContent dividers>
          {copiarResultado ? (
            <Stack spacing={1}>
              <Alert severity="success" sx={{ py: 0.25 }}>
                Usuario <strong>{copiarResultado.usuario?.nm}</strong> creado y vinculado a este cliente.
              </Alert>
              {(copiarResultado.grupos || []).map((g) => (
                <Typography key={g.nombre} variant="body2" color={g.estado === "error" ? "error" : "text.primary"}>
                  {g.estado === "creado" ? "✔" : g.estado === "omitido" ? "•" : "✖"} {g.nombre}
                  {g.motivo ? ` — ${g.motivo}` : ""}
                </Typography>
              ))}
              <Alert severity="info" sx={{ py: 0.25 }}>
                Las unidades no se copian: el mismo UID no puede existir en dos cuentas del mismo servidor.
              </Alert>
            </Stack>
          ) : (
            <Stack spacing={1.25}>
              <Typography variant="body2">
                Se crea en Leasecontrol el usuario <strong>{copiarUsuario?.nombre}</strong> y sus grupos (vacíos, sin unidades),
                vinculado a este cliente.
              </Typography>
              {copiarError && <Alert severity="error" sx={{ py: 0.25 }}>{copiarError}</Alert>}
              <TextField
                size="small"
                label="Nombre de usuario en Leasecontrol"
                helperText="Los nombres son únicos entre Wialon y Leasecontrol: si ya existe, usa otro."
                value={copiarNombre}
                onChange={(e) => setCopiarNombre(e.target.value)}
              />
              <TextField
                size="small"
                type="password"
                label="Contraseña nueva"
                value={copiarPassword}
                onChange={(e) => setCopiarPassword(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={cerrarCopiar} disabled={copiando}>{copiarResultado ? "Cerrar" : "Cancelar"}</Button>
          {!copiarResultado && (
            <Button
              size="small"
              variant="contained"
              onClick={confirmarCopiar}
              disabled={copiando || !copiarNombre.trim() || !copiarPassword}
            >
              {copiando ? <CircularProgress size={16} color="inherit" /> : "Copiar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Menu anchorEl={grupoMenuAnchor?.el} open={!!grupoMenuAnchor} onClose={() => setGrupoMenuAnchor(null)}>
        <MenuItem onClick={() => { const g = grupoMenuAnchor?.grupo; setGrupoMenuAnchor(null); setEditGrupo(g || null); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar grupo
        </MenuItem>
      </Menu>

      {editUser?.detalle && esWialon && (
        <WialonUserEditDialog
          open
          onClose={() => setEditUser(null)}
          user={{ ...editUser.detalle.usuario, local: editUser.detalle.local }}
          vinculaciones={editUser.detalle.vinculaciones}
          onUpdated={() => { const id = editUser.id; setEditUser(null); recargarUsuario(id); }}
        />
      )}
      {editUser?.detalle && !esWialon && (
        <LeasecontrolUsuarioDialog
          open
          mode="edit"
          initialUser={{ ...editUser.detalle.usuario, local: editUser.detalle.local }}
          vinculaciones={editUser.detalle.vinculaciones}
          onClose={() => setEditUser(null)}
          onSuccess={() => { const id = editUser.id; setEditUser(null); recargarUsuario(id); }}
        />
      )}
      {asignarGrupoUsuarioId && (
        <AsignarGrupoDialog
          open
          usuarioId={asignarGrupoUsuarioId}
          usuarioNombre={nombreUsuario(asignarGrupoUsuarioId)}
          listGroups={esWialon ? listWialonUnitGroups : lcListUnitGroups}
          updateGroup={esWialon ? updateWialonUnitGroup : lcUpdateUnitGroup}
          cuentas={esWialon ? undefined : cuentasDeUsuario(detalleById[asignarGrupoUsuarioId])}
          onClose={() => setAsignarGrupoUsuarioId(null)}
          onSaved={async () => { await recargarUsuario(asignarGrupoUsuarioId); setAsignarGrupoUsuarioId(null); }}
        />
      )}
      <GrupoFormDialog
        open={!!editGrupo}
        mode="edit"
        grupo={editGrupo}
        onClose={() => setEditGrupo(null)}
        onSaved={() => { setEditGrupo(null); fetchTodos(); }}
        updateGroup={esWialon ? updateWialonUnitGroup : lcUpdateUnitGroup}
        listUsers={esWialon ? listWialonUsers : lcListUsuarios}
        listUnits={esWialon ? listWialonUnits : lcListUnidades}
        providerColor={GRUPO_HEADER_COLOR}
      />
      <UnidadFormDialog
        open={!!editUnidad}
        unidad={editUnidad}
        onClose={() => setEditUnidad(null)}
        onSaved={() => { setEditUnidad(null); fetchTodos(); }}
        updateUnit={esWialon ? updateWialonUnit : lcUpdateUnidad}
        listUnits={esWialon ? listWialonUnits : lcListUnidades}
        listHwTypes={esWialon ? listWialonHwTypes : lcListHwTypes}
        listIconLibrary={esWialon ? listWialonIconLibrary : lcListIconLibrary}
        listUnitGroups={esWialon ? listWialonUnitGroups : lcListUnitGroups}
        providerColor={GRUPO_HEADER_COLOR}
      />
      <GrupoFormDialog
        open={createGrupoOpen}
        mode="create"
        onClose={() => setCreateGrupoOpen(false)}
        onSaved={() => { setCreateGrupoOpen(false); fetchTodos(); }}
        createGroup={esWialon ? createWialonUnitGroup : lcCreateUnitGroup}
        listUsers={esWialon ? listWialonUsers : lcListUsuarios}
        listUnits={esWialon ? listWialonUnits : lcListUnidades}
        initialUserIds={externalUserIds.map((eu) => eu.external_user_id)}
        providerColor={GRUPO_HEADER_COLOR}
        listCuentas={esWialon ? undefined : lcListCuentas}
        codServicioCliente={codServicioCliente}
      />
      <LeasecontrolUsuarioDialog
        open={createUserOpen}
        mode="create"
        providerName={esWialon ? "Wialon" : "Leasecontrol"}
        createUsuario={esWialon ? createWialonUser : lcCreateUsuario}
        onClose={() => setCreateUserOpen(false)}
        onSuccess={() => { setCreateUserOpen(false); recargarDetalle?.(); }}
        initialClienteServicio={codServicioCliente ? { cod_servicio_cliente: codServicioCliente } : undefined}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sección de una plataforma (lo que va dentro del panel de contenido) — pide su
// detalle una sola vez (al montarse) y despues solo cambia que pestaña muestra
// segun `tab`, sin volver a pedir nada.
// ─────────────────────────────────────────────────────────────────────────────
function PlataformaSeccion({ plataforma, tab, codServicioCliente, sortPlacaPorNumSim, sortPlacas }) {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchDetalle = useCallback(() => {
    setError("");
    return getPanelGlobalDetalle(codServicioCliente, plataforma)
      .then(({ data }) => setDetalle(data))
      .catch(() => setError("No se pudo cargar la información de la plataforma."));
  }, [codServicioCliente, plataforma]);

  useEffect(() => {
    setLoading(true);
    fetchDetalle().finally(() => setLoading(false));
  }, [fetchDetalle]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!detalle) return null;

  return (
    <Box>
      {detalle.sin_plataforma && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Este cliente todavía no está vinculado a esta plataforma. Vincula un usuario existente o registra uno nuevo.
        </Alert>
      )}
      {plataforma === "trsrastreo" ? (
        <TrsSeccion
          tab={tab}
          codServicioCliente={codServicioCliente}
          detalle={detalle}
          sortPlacas={sortPlacas}
          recargarDetalle={fetchDetalle}
        />
      ) : (
        <WlSeccion
          tab={tab}
          plataforma={plataforma}
          codServicioCliente={codServicioCliente}
          detalle={detalle}
          sortPlacaPorNumSim={sortPlacaPorNumSim}
          recargarDetalle={fetchDetalle}
        />
      )}
    </Box>
  );
}

const sidebarSx = {
  width: 240,
  flexShrink: 0,
  borderRadius: 0,
  py: 0.5,
};

const navItemSx = {
  "&.Mui-selected": { bgcolor: (theme) => theme.palette.action.selected },
};

const subNavItemSx = {
  ...navItemSx,
  pl: 5,
  py: 0.5,
  "& .MuiListItemText-primary": { fontSize: "0.85rem" },
};

export default function ClientePlataformasResumen({ codServicioCliente }) {
  const [sortPlacaPorNumSim, setSortPlacaPorNumSim] = useState(new Map());
  const [sortPlacas, setSortPlacas] = useState(new Set());
  // Plataforma desplegada en el menú lateral — exclusivo, solo una a la vez
  // (al abrir otra se cierra la anterior), independiente de cual esta seleccionada.
  const [navAbierto, setNavAbierto] = useState({ trsrastreo: true });
  // Seleccion activa: que plataforma + que subseccion se muestra en el panel derecho.
  const [seleccion, setSeleccion] = useState({ plataforma: "trsrastreo", tab: "usuarios" });
  // Cada plataforma se monta (y pide su detalle) recien la primera vez que se
  // entra a alguna de sus 3 subsecciones — despues queda montada (oculta con
  // `hidden`, nunca desmontada) para no perder lo cargado ni la pestaña elegida.
  const [montadas, setMontadas] = useState({ trsrastreo: true });

  // Vehículos activos en SORT del cliente: mismo cruce que usa Historial para
  // resaltar en verde las unidades/vehículos que ya tienen placa SORT.
  useEffect(() => {
    if (!codServicioCliente) return;
    let cancelado = false;
    getPanelGlobalVehiculos(codServicioCliente, { page: 1, limit: 200 })
      .then(({ data }) => {
        if (cancelado) return;
        const items = data.data || [];
        const mapa = new Map();
        items.forEach((v) => {
          if (v.num_sim) mapa.set(String(v.num_sim), v.placa);
          if (v.emai) mapa.set(String(v.emai), v.placa);
        });
        setSortPlacaPorNumSim(mapa);
        setSortPlacas(new Set(items.map((v) => String(v.placa || "").trim().toUpperCase()).filter(Boolean)));
      })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [codServicioCliente]);

  // Exclusivo: al abrir una plataforma, las otras 2 se cierran (solo una
  // desplegada a la vez en el menú, como un acordeón).
  const toggleNav = (key) => setNavAbierto((p) => (p[key] ? {} : { [key]: true }));

  const seleccionar = (plataforma, tab) => {
    setSeleccion({ plataforma, tab });
    setNavAbierto({ [plataforma]: true });
    setMontadas((p) => (p[plataforma] ? p : { ...p, [plataforma]: true }));
  };

  const plataformaActiva = PLATAFORMAS.find((p) => p.key === seleccion.plataforma);
  const tabActiva = SUBSECCIONES.find((t) => t.key === seleccion.tab);

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1.5, md: 2 }} sx={{ alignItems: { xs: "stretch", md: "flex-start" } }}>
      {/* Celular: plataforma y sección como pestañas deslizables (la barra lateral no cabe) */}
      <Paper variant="outlined" sx={{ display: { xs: "block", md: "none" }, borderRadius: 0, overflow: "hidden" }}>
        <Tabs
          value={seleccion.plataforma} onChange={(_, v) => seleccionar(v, seleccion.tab)} variant="scrollable" scrollButtons="auto"
          sx={{ minHeight: 40, borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { minHeight: 40, py: 0.5, fontSize: 13, fontWeight: 700, textTransform: "none" } }}
        >
          {PLATAFORMAS.map(({ key, label, color }) => (
            <Tab key={key} value={key} label={label} icon={<ComputerIcon fontSize="small" color={color} />} iconPosition="start" />
          ))}
        </Tabs>
        <Tabs
          value={seleccion.tab} onChange={(_, v) => seleccionar(seleccion.plataforma, v)} variant="fullWidth"
          sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5, fontSize: 12.5, textTransform: "none" } }}
        >
          {SUBSECCIONES.map((s) => <Tab key={s.key} value={s.key} label={s.label} />)}
        </Tabs>
      </Paper>

      <Paper variant="outlined" sx={{ ...sidebarSx, display: { xs: "none", md: "block" } }}>
        <List disablePadding>
          {PLATAFORMAS.map(({ key, label, color }) => (
            <React.Fragment key={key}>
              <ListItemButton onClick={() => toggleNav(key)} sx={navItemSx}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <ComputerIcon fontSize="small" color={color} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: "0.85rem" }}
                />
                {navAbierto[key] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </ListItemButton>
              <Collapse in={!!navAbierto[key]} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {SUBSECCIONES.map((s) => (
                    <ListItemButton
                      key={s.key}
                      selected={seleccion.plataforma === key && seleccion.tab === s.key}
                      onClick={() => seleccionar(key, s.key)}
                      sx={subNavItemSx}
                    >
                      <ListItemText primary={s.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ ...contenidoHeaderSx, display: { xs: "none", md: "flex" } }}>
          <Chip size="small" color={plataformaActiva?.color} label={plataformaActiva?.label} sx={{ fontWeight: 700 }} />
          <Typography variant="body2" color="text.secondary">{tabActiva?.label}</Typography>
        </Stack>

        {PLATAFORMAS.map(({ key }) => montadas[key] && (
          <Box key={key} hidden={seleccion.plataforma !== key}>
            <PlataformaSeccion
              plataforma={key}
              tab={seleccion.plataforma === key ? seleccion.tab : "usuarios"}
              codServicioCliente={codServicioCliente}
              sortPlacaPorNumSim={sortPlacaPorNumSim}
              sortPlacas={sortPlacas}
            />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
